
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Circle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import TranslatedText from '@/TranslatedText';

interface CustomerLoginFormProps {
  onSuccess?: () => void;
}

const customerFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type CustomerFormData = z.infer<typeof customerFormSchema>;

const CustomerLoginForm: React.FC<CustomerLoginFormProps> = ({ onSuccess }) => {
  const { loginCustomer, loginCustomerWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showAhavexerDialog, setShowAhavexerDialog] = useState(false);

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: CustomerFormData) => {
    setIsLoading(true);
    try {
      await loginCustomer(data.email, data.password);
      toast.success('Successfully logged in as customer');
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error(error);
      
      // Check if the error is related to the user being an ahavexer
      if (error.message?.includes('ahavexer account')) {
        setShowAhavexerDialog(true);
      } else {
        toast.error('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      await loginCustomerWithGoogle();
      toast.success('Successfully logged in with Google');
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error(error);
      
      // Check if the error is related to the user being an ahavexer
      if (error.message?.includes('ahavexer account')) {
        setShowAhavexerDialog(true);
      } else {
        toast.error('Google login failed. Please try again.');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      <ScrollArea className="h-full max-h-[80vh]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel><TranslatedText text="Email" /></FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel><TranslatedText text="Password" /></FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? <TranslatedText text="Logging in..." /> : <TranslatedText text="Login" />}
            </Button>

            <div className="relative my-4">
              <Separator className="absolute inset-0 m-auto" />
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  <TranslatedText text="Or continue with" />
                </span>
              </div>
            </div>

            <Button 
              type="button" 
              variant="outline" 
              className="w-full" 
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
            >
              {isGoogleLoading ? (
                <TranslatedText text="Connecting..." />
              ) : (
                <>
                  <Circle className="mr-2 h-4 w-4" />
                  <TranslatedText text="Google" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </ScrollArea>

      {/* Dialog for ahavexer account error - now with translations */}
      <Dialog open={showAhavexerDialog} onOpenChange={setShowAhavexerDialog}>
        <DialogContent>
          <DialogTitle><TranslatedText text="Ahavexer Account Detected" /></DialogTitle>
          <DialogDescription>
            <TranslatedText text="Your account appears to be an Ahavexer account, not a customer account. Please login through the Ahavexer login instead." />
          </DialogDescription>
          <div className="flex justify-end mt-4">
            <Button onClick={() => setShowAhavexerDialog(false)}>
              <TranslatedText text="Close" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerLoginForm;
